/**
 * OCR Helper - Fetches and processes OCR content from the Management API
 */

const MANAGEMENT_API_BASE = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL;
const API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

interface OCRBlock {
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  conf: number;
}

interface VersionsResponse {
  versions: string[];
  count: number;
}

interface FilesResponse {
  files: Array<{
    slug: string;
    version: string;
    ocr_url: string | null;
    markdown_url: string | null;
  }>;
  count: number;
}

/**
 * Fetches OCR content for a page and returns cleaned text (no metadata)
 * @param slug - Company slug (e.g., "konghq")
 * @param subslug - Page subslug (e.g., ".enterprise" or "" for main)
 * @returns Cleaned OCR text or null if not available
 */
export async function fetchOCRText(slug: string, subslug?: string): Promise<string | null> {
  try {
    // Build composite slug (subslug already includes leading dot if present)
    const compositeSlug = subslug ? `${slug}${subslug}` : slug;
    console.log('[OCR Helper] Fetching OCR for:', compositeSlug);

    // Step 1: Get the latest version
    const versionsUrl = `${MANAGEMENT_API_BASE}/files/${compositeSlug}/versions`;
    console.log('[OCR Helper] Fetching versions:', versionsUrl);

    const versionsResponse = await fetch(versionsUrl, {
      headers: {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (!versionsResponse.ok) {
      console.error('[OCR Helper] Versions API error:', versionsResponse.status);
      return null;
    }

    const versionsData: VersionsResponse = await versionsResponse.json();

    if (!versionsData.versions || versionsData.versions.length === 0) {
      console.error('[OCR Helper] No versions found for', compositeSlug);
      return null;
    }

    const latestVersion = versionsData.versions[0];
    console.log('[OCR Helper] Latest version:', latestVersion);

    // Step 2: Get the files for the latest version
    const filesUrl = `${MANAGEMENT_API_BASE}/files/${compositeSlug}/version/${latestVersion}`;
    console.log('[OCR Helper] Fetching files:', filesUrl);

    const filesResponse = await fetch(filesUrl, {
      headers: {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (!filesResponse.ok) {
      console.error('[OCR Helper] Files API error:', filesResponse.status);
      return null;
    }

    const filesData: FilesResponse = await filesResponse.json();

    if (!filesData.files || filesData.files.length === 0) {
      console.error('[OCR Helper] No files found for version', latestVersion);
      return null;
    }

    const ocrUrl = filesData.files[0].ocr_url;
    if (!ocrUrl) {
      console.error('[OCR Helper] No OCR URL in response');
      return null;
    }

    // Step 3: Fetch OCR JSON
    console.log('[OCR Helper] Fetching OCR from:', ocrUrl);
    const ocrResponse = await fetch(ocrUrl);

    if (!ocrResponse.ok) {
      console.error('[OCR Helper] OCR fetch error:', ocrResponse.status);
      return null;
    }

    const ocrBlocks: OCRBlock[] = await ocrResponse.json();

    // Extract just the text, stripped of metadata
    // Sort by Y position first (top to bottom), then X (left to right)
    const sortedBlocks = ocrBlocks.sort((a, b) => {
      const yDiff = a.y - b.y;
      if (Math.abs(yDiff) > 20) return yDiff; // Different lines
      return a.x - b.x; // Same line, sort by X
    });

    // Group by approximate lines and join
    const lines: string[] = [];
    let currentLine: string[] = [];
    let lastY = -100;

    for (const block of sortedBlocks) {
      if (Math.abs(block.y - lastY) > 20) {
        // New line
        if (currentLine.length > 0) {
          lines.push(currentLine.join(' '));
        }
        currentLine = [block.text];
        lastY = block.y;
      } else {
        currentLine.push(block.text);
      }
    }

    // Don't forget the last line
    if (currentLine.length > 0) {
      lines.push(currentLine.join(' '));
    }

    const cleanedText = lines.join('\n');
    console.log('[OCR Helper] Extracted', cleanedText.length, 'chars of OCR text');

    return cleanedText;
  } catch (error) {
    console.error('[OCR Helper] Error:', error);
    return null;
  }
}

/**
 * Truncates OCR text to a maximum length, trying to break at line boundaries
 */
export function truncateOCRText(text: string, maxLength: number = 4000): string {
  if (text.length <= maxLength) return text;

  // Find a good break point
  const truncated = text.substring(0, maxLength);
  const lastNewline = truncated.lastIndexOf('\n');

  if (lastNewline > maxLength * 0.8) {
    return truncated.substring(0, lastNewline) + '\n[truncated...]';
  }

  return truncated + '... [truncated]';
}

/**
 * Fetches OCR content from multiple years for historical comparison
 * Gets the latest version from each year available
 * @param slug - Company slug
 * @param subslug - Optional page subslug
 * @returns Object with year as key and OCR text as value
 */
export async function fetchMultiYearOCR(
  slug: string,
  subslug?: string
): Promise<Record<string, string>> {
  const compositeSlug = subslug ? `${slug}${subslug}` : slug;
  const result: Record<string, string> = {};

  try {
    // Get all versions
    const versionsUrl = `${MANAGEMENT_API_BASE}/files/${compositeSlug}/versions`;
    const versionsResponse = await fetch(versionsUrl, {
      headers: {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (!versionsResponse.ok) {
      console.error('[OCR Helper] Versions API error:', versionsResponse.status);
      return result;
    }

    const versionsData: VersionsResponse = await versionsResponse.json();
    if (!versionsData.versions || versionsData.versions.length === 0) {
      return result;
    }

    // Group versions by year and take first (latest) version of each year
    const versionsByYear = new Map<string, string>();
    for (const version of versionsData.versions) {
      const year = version.substring(0, 4); // YYYYMMDD -> YYYY
      if (!versionsByYear.has(year)) {
        versionsByYear.set(year, version);
      }
    }

    console.log('[OCR Helper] Fetching OCR for years:', Array.from(versionsByYear.keys()));

    // Fetch OCR for each year (in parallel)
    const fetchPromises = Array.from(versionsByYear.entries()).map(async ([year, version]) => {
      try {
        const filesUrl = `${MANAGEMENT_API_BASE}/files/${compositeSlug}/version/${version}`;
        const filesResponse = await fetch(filesUrl, {
          headers: {
            'X-API-Key': API_KEY,
            'Content-Type': 'application/json'
          }
        });

        if (!filesResponse.ok) return null;

        const filesData: FilesResponse = await filesResponse.json();
        if (!filesData.files?.[0]?.ocr_url) return null;

        const ocrResponse = await fetch(filesData.files[0].ocr_url);
        if (!ocrResponse.ok) return null;

        const ocrBlocks: OCRBlock[] = await ocrResponse.json();
        const text = processOCRBlocks(ocrBlocks);

        return { year, text };
      } catch (err) {
        console.error(`[OCR Helper] Error fetching OCR for ${year}:`, err);
        return null;
      }
    });

    const results = await Promise.all(fetchPromises);
    for (const r of results) {
      if (r) {
        result[r.year] = r.text;
      }
    }

    console.log('[OCR Helper] Fetched OCR for', Object.keys(result).length, 'years');
    return result;
  } catch (error) {
    console.error('[OCR Helper] Error fetching multi-year OCR:', error);
    return result;
  }
}

/**
 * Process OCR blocks into cleaned text
 */
function processOCRBlocks(ocrBlocks: OCRBlock[]): string {
  // Sort by Y position first (top to bottom), then X (left to right)
  const sortedBlocks = [...ocrBlocks].sort((a, b) => {
    const yDiff = a.y - b.y;
    if (Math.abs(yDiff) > 20) return yDiff;
    return a.x - b.x;
  });

  // Group by approximate lines and join
  const lines: string[] = [];
  let currentLine: string[] = [];
  let lastY = -100;

  for (const block of sortedBlocks) {
    if (Math.abs(block.y - lastY) > 20) {
      if (currentLine.length > 0) {
        lines.push(currentLine.join(' '));
      }
      currentLine = [block.text];
      lastY = block.y;
    } else {
      currentLine.push(block.text);
    }
  }

  if (currentLine.length > 0) {
    lines.push(currentLine.join(' '));
  }

  return lines.join('\n');
}
